// Safe wrapper for opening external URLs in a new tab (TCX-797).
//
// `bookingLink` (and other franchise/user-controlled values) were passed
// straight to `window.open(url, '_blank')` with no scheme check. A stored
// `javascript:`/`data:`/`vbscript:` value would execute in the opener's
// origin on click. Validate the scheme before opening and always pass
// `noopener,noreferrer` so the opened tab can't reach back via `window.opener`.
//
// Note: this guard is for absolute external URLs only. Internal router
// navigations (relative `ROUTES.*` paths) do not go through here — `new URL()`
// throws on a relative path, so route those with react-router instead.
import { message } from 'antd';

// Schemes we consider safe to hand to `window.open`. Anything else
// (javascript:, data:, vbscript:, file:, mailto:, tel:, blob:, …) is refused.
const SAFE_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Pure predicate: is `url` an absolute http(s) URL safe to open externally?
 * Returns false for empty/garbage input and for any non-http(s) scheme.
 */
export const isSafeExternalUrl = (url: unknown): boolean => {
  if (typeof url !== 'string' || url.trim() === '') return false;
  try {
    const { protocol } = new URL(url);
    return SAFE_PROTOCOLS.has(protocol);
  } catch {
    // Unparseable (relative path, malformed, etc.) — not a safe external URL.
    return false;
  }
};

/**
 * Open an external URL in a new tab, but only if its scheme is http(s).
 * Invalid/unsafe URLs are refused (no navigation) with a user-facing warning.
 *
 * Returns `true` when the URL was safe and `window.open` was invoked, `false`
 * when the URL was refused. Callers that mutate state on the way to opening a
 * link should gate that mutation on this return value, so a refused link
 * doesn't leave state changed for a navigation that never happened.
 */
export const openExternal = (url: string): boolean => {
  if (!isSafeExternalUrl(url)) {
    console.warn('openExternal: refused to open unsafe URL', url);
    message.warning('This link is unavailable or invalid.');
    return false;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};
